import { Button, Input, Modal, Space, Switch, Tabs } from "antd";
import { getAllStudentByClass, getAllStudentByYear } from "../../api/etudiants";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { getAllClass } from "../../api/pedagogicElement";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import Avvvatars from "avvvatars-react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { getAllTeachers } from "../../api/teachers";
import { getAllParents } from "../../api/parents";
import { getAllUsers } from "../../api/users";
import withPermission from "../../core/common/autorisations/withPermission";

interface Props {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (data: BrowserResult) => void;
  recipients: any;
  setRecipients: (data: any) => void;
}

type BrowserResult = {
  classIds: string[];
  studentIds: string[];
};

const RecipientBrowserModal = ({
  recipients,
  setRecipients,
  visible,
  onCancel,
  onConfirm,
}: Props) => {
  const { selectedYear } = useAcademicYear();
  const [activeKey, setActiveKey] = useState<string>("dest");
  const [classes, setClasses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [adminUser, setAdminUsers] = useState<any[]>([]);
  const [parents, setParents] = useState<any[]>([]);
  const [parentQuery, setParentQuery] = useState<string>("");
  const [classQuery, setClassQuery] = useState<string>("");
  const [studentQuery, setStudentQuery] = useState<string>("");
  const [teacherQuery, setTeacherQuery] = useState<string>("");
  const [adminUserQuery, setAdminUserQuery] = useState<string>("");
  const [loadingAdminUsers, setLoadingAdminUsers] = useState<boolean>(false);
  const [loadingParents, setLoadingParents] = useState<boolean>(false);
  const [loadingClasses, setLoadingClasses] = useState<boolean>(false);
  const [loadingStudents, setLoadingStudents] = useState<boolean>(false);
  const [loadingTeachers, setLoadingTeachers] = useState<boolean>(false);
  const [currentSelectedClassId, setCurrentSelectedClassId] =
    useState<string>("");

  const loadClasses = async () => {
    if (!selectedYear?.id) return;
    setLoadingClasses(true);
    try {
      const res = await getAllClass(
        selectedYear.id,
        buildPermissionContext(Resource.MESSAGE, Action.CREATE),
      );
      const data = res.data || [];
      const sorted =
        data.slice().sort((a: any, b: any) => {
          const l = (a?.Level?.label || "").localeCompare(
            b?.Level?.label || "",
            "fr",
            { numeric: true },
          );
          if (l !== 0) return l;
          return (a.label || "").localeCompare(b.label || "", "fr", {
            numeric: true,
          });
        }) || [];
      setClasses(sorted);
    } finally {
      setLoadingClasses(false);
    }
  };

  const loadStudentsForClass = async () => {
    setLoadingStudents(true);
    try {
      const res = await getAllStudentByClass(
        currentSelectedClassId,
        buildPermissionContext(Resource.MESSAGE, Action.CREATE),
      );
      const data = res.data || [];
      setStudents(data);
    } catch (error: any) {
      // console.log(
      //   "Erreur lors de la récupération des étudiant par Classe: ",
      //   error,
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadAllTeacher = async () => {
    try {
      setLoadingTeachers(true);
      const response = await getAllTeachers(
        buildPermissionContext(Resource.MESSAGE, Action.CREATE),
      );
      // console.log("All teachers: ", response.data);
      const data = response.data || [];
      setTeachers(data);
    } catch (error: any) {
      // console.log("Erreur lors de la récupération des enseignants: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoadingTeachers(false);
    }
  };

  const loadStudentsForYear = async () => {
    setLoadingStudents(true);
    try {
      const res = await getAllStudentByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.MESSAGE, Action.CREATE),
      );
      const data = res.data || [];
      setStudents(data);
    } catch (error: any) {
      // console.log(
      //   "Erreur lors de la récupération des étudiant par Année: ",
      //   error,
      // );
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoadingStudents(false);
    }
  };

  const loadAllParent = async () => {
    setLoadingParents(true);
    try {
      const response = await getAllParents(
        buildPermissionContext(Resource.MESSAGE, Action.CREATE),
      );
      // console.log("All parents: ", response.data);
      setParents(response.data);
    } catch (error: any) {
      // console.log("Erreur lors de la récupération des parents: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoadingParents(false);
    }
  };

  const loadAdminUsers = async () => {
    setLoadingAdminUsers(true);
    try {
      const response = await getAllUsers(
        buildPermissionContext(Resource.MESSAGE, Action.CREATE),
      );
      console.log("All user admin: ", response);
      setAdminUsers(response);
    } catch (error: any) {
      console.log("Erreur lors de la récupération des admins: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  const toggleClass = (id: string) => {
    const exists = recipients.ClassIds.includes(id);
    const nextClassIds = exists
      ? recipients.ClassIds.filter((x: string) => x !== id)
      : [...recipients.ClassIds, id];

    // Build a list of student IDs that belong to the toggled class
    const studentIdsInClass = students
      .filter((s) => {
        const classId = s?.Registrations?.[0]?.Class?.id ?? s?.Class?.id;
        return String(classId) === id;
      })
      .map((s) => String(s?.StudentProfil?.id ?? s?.id));

    const nextStudentIds = exists
      ? recipients.StudentIds
      : recipients.StudentIds.filter(
          (sid: any) => !studentIdsInClass.includes(sid),
        );

    setRecipients({
      ...recipients,
      ClassIds: nextClassIds,
      StudentIds: nextStudentIds,
    });
  };

  useEffect(() => {
    loadAllTeacher();
    loadAllParent();
    loadAdminUsers();
  }, []);

  useEffect(() => {
    if (!currentSelectedClassId) {
      loadStudentsForYear();
      return;
    }
    loadStudentsForClass();
  }, [currentSelectedClassId]);

  const selecteClass = (id: string) => {
    setCurrentSelectedClassId(id);
    setActiveKey("students");
  };

  const filteredClasses = classes.filter((c) =>
    `${c?.Level?.abbreviation || ""} ${c?.label || ""}`
      .toLowerCase()
      .includes(classQuery.toLowerCase()),
  );
  const filteredStudents = students.filter((s) =>
    `${s?.StudentProfil?.firstname || s?.firstname || ""} ${
      s?.StudentProfil?.lastname || s?.lastname || ""
    }`
      .toLowerCase()
      .includes(studentQuery.toLowerCase()),
  );
  const filteredParents = parents.filter((p) =>
    `${p?.firstname || ""} ${p?.lastname || ""}`
      .toLowerCase()
      .includes(parentQuery.toLowerCase()),
  );
  const filteredAdminUsers = adminUser.filter((u) =>
    `${u?.firstname || ""} ${u?.lastname || ""}`
      .toLowerCase()
      .includes(adminUserQuery.toLowerCase()),
  );

  useEffect(() => {
    console.log("recipients", recipients);
  }, [recipients]);

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span>Sélectionnez les destinataires</span>
          <span style={{ marginLeft: "auto", fontSize: 13 }}>
            {(recipients.ClassIds?.length || 0) +
              (recipients.StudentIds?.length || 0) +
              (recipients.TeacherIds?.length || 0) +
              (recipients.ParentIds?.length || 0) +
              (recipients.StaffIds?.length || 0)}{" "}
            sélectionné(s)
          </span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={720}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div style={{ fontSize: 13 }}>
            {recipients.ClassIds?.length || 0} classe(s) •{" "}
            {recipients.StudentIds?.length || 0} étudiant(s) •{" "}
            {recipients.TeacherIds?.length || 0} professeur(s) •{" "}
            {recipients.ParentIds?.length || 0} parent(s) •{" "}
            {recipients.StaffIds?.length || 0} admin(s)
          </div>
          <Space>
            <Button onClick={onCancel}>Fermer</Button>
            <Button
              type="primary"
              onClick={() =>
                onConfirm({
                  classIds: recipients.ClassIds,
                  studentIds: recipients.StudentIds,
                })
              }
            >
              Valider
            </Button>
          </Space>
        </div>
      }
      afterOpenChange={(opened) => {
        if (opened) loadClasses();
      }}
    >
      <Tabs
        activeKey={activeKey}
        onChange={setActiveKey}
        items={[
          {
            key: "dest",
            label: "Destinataires",
            children: (
              <div className="row g-2">
                <div className="col-12 col-md-6 col-lg-4">
                  <div
                    className="card h-100"
                    style={{
                      backgroundColor: "rgba(165,255,0,0.20)",
                      border: "2px solid #B0BEDD",
                      cursor: "pointer",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      height: 110,
                      maxWidth: 220,
                    }}
                    onClick={() => setActiveKey("staff")}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center p-2">
                      <ImageWithBasePath
                        src="assets/img/icons/staff.svg"
                        alt="Folder"
                        className="mb-2"
                        height={48}
                        width={48}
                      />
                      <span className="fw-bold" style={{ color: "#00cc00" }}>
                        Admin Membres
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div
                    className="card h-100"
                    style={{
                      backgroundColor: "rgba(0,165,255,0.20)",
                      border: "2px solid #B0BEDD",
                      cursor: "pointer",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      height: 110,
                      maxWidth: 220,
                    }}
                    onClick={() => setActiveKey("teachers")}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center p-2">
                      <ImageWithBasePath
                        src="assets/img/icons/teacher.svg"
                        alt="Folder"
                        className="mb-2"
                        height={48}
                        width={48}
                      />
                      <span className="fw-bold" style={{ color: "#0066cc" }}>
                        Professeurs
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div
                    className="card h-100"
                    style={{
                      backgroundColor: "rgba(255, 105, 180, 0.20)",
                      border: "2px solid #B0BEDD",
                      cursor: "pointer",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      height: 120,
                      maxWidth: 240,
                    }}
                    onClick={() => setActiveKey("parents")}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center p-2">
                      <ImageWithBasePath
                        src="assets/img/icons/parent.webp"
                        height={56}
                        width={56}
                        alt="Parents"
                        className="mb-2"
                      />
                      <span className="fw-bold" style={{ color: "#ff1493" }}>
                        Parents
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div
                    className="card h-100"
                    style={{
                      backgroundColor: "rgba(147, 112, 219, 0.20)",
                      border: "2px solid #B0BEDD",
                      cursor: "pointer",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      height: 110,
                      maxWidth: 220,
                    }}
                    onClick={() => setActiveKey("classes")}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center p-2">
                      <ImageWithBasePath
                        src="assets/img/icons/subject.svg"
                        alt="Folder"
                        className="mb-2"
                        height={48}
                        width={48}
                      />
                      <span className="fw-bold" style={{ color: "#9370DB" }}>
                        Classes
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div
                    className="card h-100"
                    style={{
                      backgroundColor: "rgba(255,165,0,0.20)",
                      border: "2px solid #B0BEDD",
                      cursor: "pointer",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                      height: 110,
                      maxWidth: 220,
                    }}
                    onClick={() => setActiveKey("students")}
                  >
                    <div className="card-body d-flex flex-column align-items-center justify-content-center p-2">
                      <ImageWithBasePath
                        src="assets/img/icons/student.svg"
                        alt="Folder"
                        className="mb-2"
                        height={48}
                        width={48}
                      />
                      <span className="fw-bold" style={{ color: "#cc5500" }}>
                        Étudiants
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ),
          },
          {
            key: "classes",
            label: "Classes",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.Search
                  value={classQuery}
                  onChange={(e) => setClassQuery(e.target.value)}
                  placeholder="Rechercher une classe"
                  allowClear
                />
                <div className="col-12 mb-2 d-flex align-items-center justify-content-between">
                  <span className="fw-bold" style={{ color: "#9370DB" }}>
                    Toutes les classes
                  </span>
                  <Switch
                    checked={
                      filteredClasses.length > 0 &&
                      filteredClasses.every((c) =>
                        recipients.ClassIds.includes(String(c.id)),
                      )
                    }
                    onChange={(checked) => {
                      if (checked) {
                        setRecipients({
                          ...recipients,
                          ClassIds: filteredClasses.map((c) => String(c.id)),
                          StudentIds: [],
                        });
                      } else {
                        setRecipients({
                          ...recipients,
                          ClassIds: [],
                        });
                      }
                    }}
                  />
                </div>
                <div
                  className="row row-cols-2 row-cols-md-3 row-cols-xl-4 g-3"
                  style={{
                    maxHeight: 360,
                    overflowY: "auto",
                  }}
                >
                  {loadingClasses ? (
                    <div className="text-center p-3">Chargement...</div>
                  ) : filteredClasses.length === 0 ? (
                    <div className="text-center p-3">Aucune classe</div>
                  ) : (
                    filteredClasses.map((cls) => (
                      <div key={cls.id} className="col">
                        <div
                          className={`card h-100 ${
                            recipients.ClassIds.includes(String(cls.id)) ||
                            recipients.allClass
                              ? "border-primary border-2"
                              : ""
                          }`}
                          onClick={() => toggleClass(String(cls.id))}
                          onDoubleClick={() => selecteClass(String(cls.id))}
                          style={{
                            cursor: "pointer",
                            backgroundColor: "#f7f9fc",
                            border: "1px solid #E3E6EF",
                            height: 90,
                            maxWidth: 220,
                            margin: "0 auto",
                          }}
                        >
                          <div className="card-body text-center">
                            <div className="fw-bold">
                              {cls?.Level?.abbreviation}
                            </div>
                            <div className="text-muted small">{cls.label}</div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </Space>
            ),
          },
          {
            key: "students",
            label: "Étudiants",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                {currentSelectedClassId && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 10px",
                      background: "#f5f5f5",
                      borderRadius: 6,
                      fontSize: 13,
                    }}
                  >
                    <span>
                      Classe :{" "}
                      <strong>
                        {
                          classes.find(
                            (c) => String(c.id) === currentSelectedClassId,
                          )?.Level?.abbreviation
                        }{" "}
                        {
                          classes.find(
                            (c) => String(c.id) === currentSelectedClassId,
                          )?.label
                        }
                      </strong>
                    </span>
                    <Button
                      size="small"
                      type="text"
                      icon={<span style={{ fontSize: 12 }}>↺</span>}
                      onClick={() => {
                        setCurrentSelectedClassId("");
                        setActiveKey("classes");
                      }}
                      style={{ marginLeft: 8 }}
                    >
                      Réinitialiser
                    </Button>
                  </div>
                )}
                <Input.Search
                  value={studentQuery}
                  onChange={(e) => setStudentQuery(e.target.value)}
                  placeholder="Rechercher un étudiant"
                  allowClear
                />
                <div className="col-12 mb-2 d-flex align-items-center justify-content-between">
                  <span className="fw-bold" style={{ color: "#cc5500" }}>
                    Tous les étudiants
                  </span>
                  <Switch
                    checked={
                      currentSelectedClassId
                        ? students.length > 0 &&
                          students
                            .map((s) => String(s?.StudentProfil?.id ?? s?.id))
                            .every((sid) => recipients.StudentIds.includes(sid))
                        : students.length > 0 &&
                          students
                            .map((s) => String(s?.StudentProfil?.id ?? s?.id))
                            .every((sid) => recipients.StudentIds.includes(sid))
                    }
                    onChange={(checked) => {
                      if (currentSelectedClassId) {
                        const currentStudentIds = students.map((s) =>
                          String(s?.StudentProfil?.id ?? s?.id),
                        );
                        if (checked) {
                          setRecipients({
                            ...recipients,
                            ClassIds: recipients.ClassIds.filter(
                              (cid: any) => cid !== currentSelectedClassId,
                            ),
                            StudentIds: Array.from(
                              new Set([
                                ...recipients.StudentIds,
                                ...currentStudentIds,
                              ]),
                            ),
                          });
                        } else {
                          setRecipients({
                            ...recipients,
                            StudentIds: recipients.StudentIds.filter(
                              (sid: any) => !currentStudentIds.includes(sid),
                            ),
                          });
                        }
                      } else {
                        if (checked) {
                          setRecipients({
                            ...recipients,
                            ClassIds: [],
                            StudentIds: students.map((s) =>
                              String(s?.StudentProfil?.id ?? s?.id),
                            ),
                          });
                        } else {
                          setRecipients({
                            ...recipients,
                            StudentIds: [],
                          });
                        }
                      }
                    }}
                  />
                </div>
                <div
                  className="row row-cols-2 row-cols-md-3 row-cols-xl-4 g-3"
                  style={{
                    maxHeight: 460,
                    overflowY: "auto",
                  }}
                >
                  {loadingStudents ? (
                    <div className="text-center p-4 text-muted w-100">
                      Chargement...
                    </div>
                  ) : filteredStudents.length === 0 ? (
                    <div className="text-center p-4 text-muted w-100">
                      Aucun étudiant
                    </div>
                  ) : (
                    filteredStudents.map((s) => {
                      const sid = s?.StudentProfil?.id ?? s?.id;
                      const firstname =
                        s?.StudentProfil?.firstname ?? s?.firstname;
                      const lastname =
                        s?.StudentProfil?.lastname ?? s?.lastname;
                      const levelAbbr =
                        s?.Registrations?.[0]?.Class?.Level?.abbreviation ??
                        s?.Level?.abbreviation;
                      const classLabel = s?.Registrations?.[0]?.Class?.label;
                      const selected = recipients.StudentIds.includes(
                        String(sid),
                      );
                      return (
                        <div key={sid} className="col">
                          <div
                            className={`card h-100 ${
                              selected || recipients.allStudents
                                ? "border-primary border-2"
                                : ""
                            }`}
                            onClick={() => {
                              const idStr = String(sid);
                              const exists =
                                recipients.StudentIds.includes(idStr);
                              if (exists) {
                                setRecipients({
                                  ...recipients,
                                  StudentIds: recipients.StudentIds.filter(
                                    (x: any) => x !== idStr,
                                  ),
                                });
                              } else {
                                const classId =
                                  s?.Registrations?.[0]?.Class?.id ??
                                  s?.Class?.id;
                                setRecipients({
                                  ...recipients,
                                  ClassIds: recipients.ClassIds.filter(
                                    (cid: any) => cid !== String(classId || ""),
                                  ),
                                  StudentIds: [...recipients.StudentIds, idStr],
                                });
                              }
                            }}
                            style={{
                              cursor: "pointer",
                              backgroundColor: "#f7f9fc",
                              border: "1px solid #E3E6EF",
                              height: 140,
                              maxWidth: 220,
                              margin: "0 auto",
                            }}
                          >
                            <div className="card-body text-center">
                              <div className="mb-3">
                                <span className="avatar avatar-md rounded-circle">
                                  <Avvvatars
                                    value={`${firstname?.charAt(0) || ""}${
                                      lastname?.charAt(0) || ""
                                    }`}
                                    size={28}
                                    style="character"
                                  />
                                </span>
                              </div>
                              <div className="fw-bold">
                                {firstname} {lastname}
                              </div>
                              <div className="text-muted small">
                                {s?.StudentProfil?.matricule || s?.matricule}
                              </div>
                              <p className="text-muted small mb-0">
                                {levelAbbr} - {classLabel}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Space>
            ),
          },
          {
            key: "teachers",
            label: "Professeurs",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.Search
                  value={teacherQuery}
                  onChange={(e) => setTeacherQuery(e.target.value)}
                  placeholder="Rechercher un professeur"
                  allowClear
                />
                <div className="col-12 mb-2 d-flex align-items-center justify-content-between">
                  <span className="fw-bold" style={{ color: "#0066cc" }}>
                    Tous les professeurs
                  </span>
                  <Switch
                    checked={
                      recipients.TeacherIds &&
                      recipients.TeacherIds.length > 0 &&
                      teachers.every((t) => {
                        const tid = String(
                          t?.TeacherProfil?.id ?? t?.UserProfil?.id ?? t?.id,
                        );
                        return recipients.TeacherIds.includes(tid);
                      })
                    }
                    onChange={(checked) =>
                      setRecipients({
                        ...recipients,
                        TeacherIds: checked
                          ? teachers.map((t) =>
                              String(
                                t?.TeacherProfil?.id ??
                                  t?.UserProfil?.id ??
                                  t?.id,
                              ),
                            )
                          : [],
                      })
                    }
                  />
                </div>
                <div
                  className="row row-cols-2 row-cols-md-3 row-cols-xl-4 g-3"
                  style={{ maxHeight: 360, overflowY: "auto" }}
                >
                  {loadingTeachers ? (
                    <div className="text-center p-3 w-100">Chargement...</div>
                  ) : teachers.filter((t) =>
                      `${t?.TeacherProfil?.firstname || t?.firstname || ""} ${
                        t?.TeacherProfil?.lastname || t?.lastname || ""
                      }`
                        .toLowerCase()
                        .includes(teacherQuery.toLowerCase()),
                    ).length === 0 ? (
                    <div className="text-center p-3 w-100">
                      Aucun professeur
                    </div>
                  ) : (
                    <>
                      {teachers
                        .filter((t) =>
                          `${t?.TeacherProfil?.firstname || t?.firstname || ""} ${
                            t?.TeacherProfil?.lastname || t?.lastname || ""
                          }`
                            .toLowerCase()
                            .includes(teacherQuery.toLowerCase()),
                        )
                        .map((t) => {
                          const tid = String(
                            t?.TeacherProfil?.id ?? t?.UserProfil?.id ?? t?.id,
                          );
                          const firstname =
                            t?.TeacherProfil?.firstname ??
                            t?.UserProfil?.firstname ??
                            t?.firstname;
                          const lastname =
                            t?.TeacherProfil?.lastname ??
                            t?.UserProfil?.lastname ??
                            t?.lastname;
                          const email =
                            t?.TeacherProfil?.email ??
                            t?.UserProfil?.email ??
                            t?.email;
                          const selected = (
                            recipients.TeacherIds || []
                          ).includes(tid);
                          return (
                            <div key={tid} className="col">
                              <div
                                className={`card h-100 ${
                                  selected ? "border-primary border-2" : ""
                                }`}
                                onClick={() => {
                                  const exists = (
                                    recipients.TeacherIds || []
                                  ).includes(tid);
                                  const next = exists
                                    ? (recipients.TeacherIds || []).filter(
                                        (x: any) => x !== tid,
                                      )
                                    : [...(recipients.TeacherIds || []), tid];
                                  setRecipients({
                                    ...recipients,
                                    TeacherIds: next,
                                  });
                                }}
                                style={{
                                  cursor: "pointer",
                                  backgroundColor: "#f7f9fc",
                                  border: "1px solid #E3E6EF",
                                  height: 130,
                                  maxWidth: 220,
                                  margin: "0 auto",
                                }}
                              >
                                <div className="card-body text-center">
                                  <div className="mb-2">
                                    <span className="avatar avatar-md rounded-circle">
                                      <Avvvatars
                                        value={`${firstname?.charAt(0) || ""}${
                                          lastname?.charAt(0) || ""
                                        }`}
                                        style="character"
                                        size={28}
                                      />
                                    </span>
                                  </div>
                                  <div className="fw-bold">
                                    {firstname} {lastname}
                                  </div>
                                  <div className="text-muted small">
                                    {email}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>
              </Space>
            ),
          },
          {
            key: "parents",
            label: "Parents",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.Search
                  value={parentQuery}
                  onChange={(e) => setParentQuery(e.target.value)}
                  placeholder="Rechercher un parent"
                  allowClear
                />
                <div className="col-12 mb-2 d-flex align-items-center justify-content-between">
                  <span className="fw-bold" style={{ color: "#ff1493" }}>
                    Tous les parents
                  </span>
                  <Switch
                    checked={
                      filteredParents.length > 0 &&
                      filteredParents.every((p) =>
                        (recipients.ParentIds || []).includes(String(p.id)),
                      )
                    }
                    onChange={(checked) =>
                      setRecipients({
                        ...recipients,
                        ParentIds: checked
                          ? filteredParents.map((p) => String(p.id))
                          : [],
                      })
                    }
                  />
                </div>
                <div
                  className="row row-cols-2 row-cols-md-3 row-cols-xl-4 g-3"
                  style={{ maxHeight: 360, overflowY: "auto" }}
                >
                  {loadingParents ? (
                    <div className="text-center p-3 w-100">Chargement...</div>
                  ) : filteredParents.length === 0 ? (
                    <div className="text-center p-3 w-100">Aucun parent</div>
                  ) : (
                    filteredParents.map((p) => {
                      const pid = String(p.id);
                      const firstname = p.firstname;
                      const lastname = p.lastname;
                      const email = p.email;
                      const number = p.number;
                      const selected = (recipients.ParentIds || []).includes(
                        pid,
                      );
                      return (
                        <div key={pid} className="col">
                          <div
                            className={`card h-100 ${
                              selected ? "border-primary border-2" : ""
                            }`}
                            onClick={() => {
                              const exists = (
                                recipients.ParentIds || []
                              ).includes(pid);
                              const next = exists
                                ? (recipients.ParentIds || []).filter(
                                    (x: any) => x !== pid,
                                  )
                                : [...(recipients.ParentIds || []), pid];
                              setRecipients({
                                ...recipients,
                                ParentIds: next,
                              });
                            }}
                            style={{
                              cursor: "pointer",
                              backgroundColor: "#f7f9fc",
                              border: "1px solid #E3E6EF",
                              height: 130,
                              maxWidth: 220,
                              margin: "0 auto",
                            }}
                          >
                            <div className="card-body text-center">
                              <div className="mb-2">
                                <span className="avatar avatar-md rounded-circle">
                                  <Avvvatars
                                    value={`${firstname?.charAt(0) || ""}${
                                      lastname?.charAt(0) || ""
                                    }`}
                                    style="character"
                                    size={28}
                                  />
                                </span>
                              </div>
                              <div className="fw-bold">
                                {firstname} {lastname}
                              </div>
                              <div className="text-muted small">{email}</div>
                              <div className="text-muted small">{number}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Space>
            ),
          },
          {
            key: "staff",
            label: "Admin Membres",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.Search
                  value={adminUserQuery}
                  onChange={(e) => setAdminUserQuery(e.target.value)}
                  placeholder="Rechercher un membre"
                  allowClear
                />
                <div className="col-12 mb-2 d-flex align-items-center justify-content-between">
                  <span className="fw-bold" style={{ color: "#00cc00" }}>
                    Tous les membres admin
                  </span>
                  <Switch
                    checked={
                      filteredAdminUsers.length > 0 &&
                      filteredAdminUsers.every((u) =>
                        (recipients.StaffIds || []).includes(String(u.id)),
                      )
                    }
                    onChange={(checked) =>
                      setRecipients({
                        ...recipients,
                        StaffIds: checked
                          ? filteredAdminUsers.map((u) => String(u.id))
                          : [],
                      })
                    }
                  />
                </div>
                <div
                  className="row row-cols-2 row-cols-md-3 row-cols-xl-4 g-3"
                  style={{ maxHeight: 360, overflowY: "auto" }}
                >
                  {loadingAdminUsers ? (
                    <div className="text-center p-3 w-100">Chargement...</div>
                  ) : filteredAdminUsers.length === 0 ? (
                    <div className="text-center p-3 w-100">
                      Aucun membre administratif
                    </div>
                  ) : (
                    filteredAdminUsers.map((u) => {
                      const uid = String(u.id);
                      const firstname = u.firstname;
                      const lastname = u.lastname;
                      const email = u.email;
                      const jobFunction = u.jobFunction;
                      const departmentLabel = u?.Department?.label;
                      const selected = (recipients.StaffIds || []).includes(
                        uid,
                      );
                      return (
                        <div key={uid} className="col">
                          <div
                            className={`card h-100 ${
                              selected ? "border-primary border-2" : ""
                            }`}
                            onClick={() => {
                              const exists = (
                                recipients.StaffIds || []
                              ).includes(uid);
                              const next = exists
                                ? (recipients.StaffIds || []).filter(
                                    (x: any) => x !== uid,
                                  )
                                : [...(recipients.StaffIds || []), uid];
                              setRecipients({
                                ...recipients,
                                StaffIds: next,
                              });
                            }}
                            style={{
                              cursor: "pointer",
                              backgroundColor: "#f7f9fc",
                              border: "1px solid #E3E6EF",
                            }}
                          >
                            <div className="card-body text-center">
                              <div className="mb-2">
                                <span className="avatar avatar-md rounded-circle">
                                  <Avvvatars
                                    value={`${firstname?.charAt(0) || ""}${
                                      lastname?.charAt(0) || ""
                                    }`}
                                    style="character"
                                    size={28}
                                  />
                                </span>
                              </div>
                              <div className="fw-bold">
                                {firstname} {lastname}
                              </div>
                              <div className="text-muted small">{email}</div>
                              <div className="text-muted small">
                                {jobFunction}
                                {departmentLabel ? ` • ${departmentLabel}` : ""}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  );
};

export default withPermission(
  RecipientBrowserModal,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.MESSAGE, action),
    ),
  ],
  "masquer",
);
